type PriLevel = "gp" | "bs" | "zp" | "state"
type PriTenure = "current" | "previous"

function normalizeSegment(value: string | number | null | undefined): string {
  const v = String(value ?? "").trim().toLowerCase()
  if (!v) return ""
  return v
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function hash8(input: string): string {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return (h >>> 0).toString(16).padStart(8, "0")
}

export type PriPosition =
  | "chief-minister"
  | "panchayat-minister"
  | "sarpanch"
  | "chairperson"
  | "vice-chairperson"
  | "ward-member"
  | "eminent"
  | "dignitary"

export type BuildPriEntityIdArgs = {
  level: PriLevel
  tenure: PriTenure
  position: PriPosition
  district?: string
  block?: string
  village?: string
  wardNumber?: string | number
  personKey?: string
}

export function buildPriEntityId(args: BuildPriEntityIdArgs): string {
  const lvl = args.level
  const tenure = args.tenure
  const position = args.position

  const d = normalizeSegment(args.district)
  const b = normalizeSegment(args.block)
  const v = normalizeSegment(args.village)
  const w = args.wardNumber === undefined || args.wardNumber === null ? "" : normalizeSegment(args.wardNumber)
  const p = normalizeSegment(args.personKey)

  if (!d) return ""
  if (lvl === "gp" && (!b || !v)) return ""
  if (lvl === "bs" && !b) return ""

  return [
    "pri",
    lvl,
    tenure,
    position,
    d,
    b || "_",
    v || "_",
    w || "_",
    p || "_",
  ].join(":")
}

export function buildEminentPersonEntityId(args: {
  level: PriLevel
  tenure: PriTenure
  district?: string
  block?: string
  village?: string
  name: string
}): string {
  const nameSlug = normalizeSegment(args.name)
  if (!nameSlug) return ""
  const context = [
    normalizeSegment(args.level),
    normalizeSegment(args.tenure),
    normalizeSegment(args.district),
    normalizeSegment(args.block),
    normalizeSegment(args.village),
  ].join("|")
  const h = hash8(context)
  return buildPriEntityId({
    level: args.level,
    tenure: args.tenure,
    position: "eminent",
    district: args.district,
    block: args.block,
    village: args.village,
    personKey: `${nameSlug}-${h}`,
  })
}

export type DignitaryKey = "cm_nayab_saini" | "minister_panwar"

export const MASTER_DIGNITARY_ENTITY_ID: Record<DignitaryKey, string> = {
  cm_nayab_saini: buildPriEntityId({
    level: "state",
    tenure: "current",
    position: "dignitary",
    district: "haryana",
    personKey: "nayab-singh-saini",
  }),
  minister_panwar: buildPriEntityId({
    level: "state",
    tenure: "current",
    position: "dignitary",
    district: "haryana",
    personKey: "krishan-lal-panwar",
  }),
}

export function getDignitaryEntityId(key: DignitaryKey): string {
  return MASTER_DIGNITARY_ENTITY_ID[key]
}

export function canonicalizeLegacyPriEntityId(entityId: string): string {
  const raw = String(entityId || "")
  if (raw.startsWith("pri:")) return raw

  const gp = raw.match(/^leader-gp-([a-z0-9-]+)-([a-z0-9-]+)-([a-z0-9-]+)-(.+)$/)
  if (gp) {
    const [, d, b, v, suffix] = gp
    const position: PriPosition = suffix.includes("vice-") ? "vice-chairperson" : "sarpanch"
    return buildPriEntityId({
      level: "gp",
      tenure: "current",
      position,
      district: d,
      block: b,
      village: v,
      personKey: suffix,
    })
  }

  const bs = raw.match(/^leader-bs-([a-z0-9-]+)-([a-z0-9-]+)-(.+)$/)
  if (bs) {
    const [, d, b, suffix] = bs
    const position: PriPosition = suffix.includes("vice-") ? "vice-chairperson" : "chairperson"
    return buildPriEntityId({
      level: "bs",
      tenure: "current",
      position,
      district: d,
      block: b,
      personKey: suffix,
    })
  }

  const zp = raw.match(/^leader-zp-([a-z0-9-]+)-(.+)$/)
  if (zp) {
    const [, d, suffix] = zp
    const position: PriPosition = suffix.includes("vice-") ? "vice-chairperson" : "chairperson"
    return buildPriEntityId({
      level: "zp",
      tenure: "current",
      position,
      district: d,
      personKey: suffix,
    })
  }

  const ward = raw.match(/^ward-(gp|bs|zp)-([a-z0-9-]+)-([a-z0-9-]+)-([a-z0-9-]+)-(\d+)$/)
  if (ward) {
    const [, lvl, d, b, v, w] = ward as unknown as [string, PriLevel, string, string, string, string]
    return buildPriEntityId({
      level: lvl,
      tenure: "current",
      position: "ward-member",
      district: d,
      block: b,
      village: v,
      wardNumber: w,
      personKey: `ward-${w}`,
    })
  }

  return raw
}
